import { useMemo, useState } from "react";
import {
  Box,
  Button,
  ButtonGroup,
  Checkbox,
  FormControlLabel,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import {
  calculateHardChdRisk,
  CholesterolUnit,
  convertCholesterol,
  formatHardChdRiskPct,
  FraminghamSex,
  riskTierFromPct,
} from "../../../utils/framinghamHardChdScore";

export default function FraminghamHardChdRiskComponent() {
  const [unitSystem, setUnitSystem] = useState<CholesterolUnit>("mgdl");
  const [sex, setSex] = useState<FraminghamSex>("male");
  const [age, setAge] = useState(55);
  const [totalCholesterol, setTotalCholesterol] = useState("200");
  const [hdlCholesterol, setHdlCholesterol] = useState("50");
  const [systolicBp, setSystolicBp] = useState("130");
  const [treatedBp, setTreatedBp] = useState(false);
  const [smoker, setSmoker] = useState(false);

  const handleToggleUnitSystem = (newSystem: CholesterolUnit) => {
    if (newSystem === unitSystem) return;
    const tc = convertCholesterol(
      Number(totalCholesterol),
      unitSystem,
      newSystem,
    );
    const hdl = convertCholesterol(
      Number(hdlCholesterol),
      unitSystem,
      newSystem,
    );
    setUnitSystem(newSystem);
    if (tc != null) setTotalCholesterol(String(tc));
    if (hdl != null) setHdlCholesterol(String(hdl));
  };

  const result = useMemo(
    () =>
      calculateHardChdRisk({
        sex,
        age,
        totalCholesterol: Number(totalCholesterol),
        hdlCholesterol: Number(hdlCholesterol),
        systolicBp: Number(systolicBp),
        treatedBp,
        smoker,
        unitSystem,
      }),
    [
      sex,
      age,
      totalCholesterol,
      hdlCholesterol,
      systolicBp,
      treatedBp,
      smoker,
      unitSystem,
    ],
  );
  const tier = result ? riskTierFromPct(result.riskPct) : null;
  const unitLabel = unitSystem === "mgdl" ? "mg/dL" : "mmol/L";

  return (
    <Box display="flex" flexDirection="column" height="100%">
      <Box flex={1} overflow="auto" p={2}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant="body2" color="textSecondary" mb={1}>
              Framingham 10-year Hard CHD risk (ATP III / MDCalc). Validated for
              ages 20–79 without prior CHD or diabetes.
            </Typography>
            <ButtonGroup size="small">
              <Button
                variant={unitSystem === "mgdl" ? "contained" : "outlined"}
                onClick={() => handleToggleUnitSystem("mgdl")}
              >
                mg/dL
              </Button>
              <Button
                variant={unitSystem === "mmoll" ? "contained" : "outlined"}
                onClick={() => handleToggleUnitSystem("mmoll")}
              >
                mmol/L
              </Button>
            </ButtonGroup>
          </Grid>

          <Grid item xs={12} sm={6}>
            <ButtonGroup fullWidth size="small">
              <Button
                variant={sex === "male" ? "contained" : "outlined"}
                onClick={() => setSex("male")}
              >
                Male
              </Button>
              <Button
                variant={sex === "female" ? "contained" : "outlined"}
                onClick={() => setSex("female")}
              >
                Female
              </Button>
            </ButtonGroup>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="number"
              size="small"
              label="Age (years)"
              value={age}
              onChange={(e) => setAge(Number(e.target.value))}
              inputProps={{ min: 20, max: 79 }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="number"
              size="small"
              label={`Total cholesterol (${unitLabel})`}
              value={totalCholesterol}
              onChange={(e) => setTotalCholesterol(e.target.value)}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="number"
              size="small"
              label={`HDL cholesterol (${unitLabel})`}
              value={hdlCholesterol}
              onChange={(e) => setHdlCholesterol(e.target.value)}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              type="number"
              size="small"
              label="Systolic BP (mmHg)"
              value={systolicBp}
              onChange={(e) => setSystolicBp(e.target.value)}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={treatedBp}
                  onChange={(e) => setTreatedBp(e.target.checked)}
                />
              }
              label="On BP medication?"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={smoker}
                  onChange={(e) => setSmoker(e.target.checked)}
                />
              }
              label="Current smoker?"
            />
          </Grid>
        </Grid>
      </Box>

      <Box
        p={2}
        sx={{
          borderTop: 1,
          borderColor: "#D3D3D3",
        }}
      >
        <Typography variant="h5">
          {result && tier
            ? `${formatHardChdRiskPct(result.riskPct)} — ${tier.label}`
            : "Enter required values"}
        </Typography>
        <Typography variant="body2" color="textSecondary">
          {result && tier
            ? `${result.points} points · ${tier.description}`
            : "Age, cholesterol, and blood pressure are needed to calculate risk."}
        </Typography>
      </Box>
    </Box>
  );
}
